// Beta Report wording (see ./schema.js), in the four interface languages. Questions
// follow the product owner's brief; the English labels are also sent with each
// submission so the emailed report reads the same whatever language the tester used.

const EN = {
  ui: {
    eyebrow: "Beta Report",
    title: "Tell us about this interview",
    intro: "This takes about 3–5 minutes. Your answers help us fix problems and improve the Interview Simulator. Please submit it before starting your next interview.",
    mode: "Interview mode",
    modes: { guided: "Guided Mode", live: "Advanced Mode" },
    stage: (n, total) => `Step ${n} of ${total}`,
    progress: "Progress",
    next: "Next",
    back: "Back",
    later: "Fill in later",
    submit: "Submit Beta Report",
    submitting: "Sending…",
    required: "Required",
    optional: "Optional",
    missing: "Please answer the questions marked as required.",
    submitted: "Thank you! Your Beta Report was sent.",
    submitFail: "We couldn't send your Beta Report. Please try again.",
    charsLeft: (n) => `${n} characters left`,
    pickQuestion: "Question number (optional)",
    noQuestion: "Not sure / several",
    languageAuto: "Interface language (detected automatically)",
    na: "N/A",
    alreadySent: "You've already sent the Beta Report for this interview. Thank you!",
    backToReport: "Back to the report",
    pending: "Please submit the Beta Report for your last interview before starting a new one.",
    pendingOpen: "Open the Beta Report",
    ratingWhy: "What could be better? Please give details.",
    ratingWhyHint: "For example: what happened, and on which screen or question.",
  },
  stages: { experience: "Experience", interview: "Interview", feedback: "Feedback", technical: "Technical", final: "Final comments" },
  sections: {
    A: "Overall experience", B: "App functionality and errors", C: "Interview question quality",
    D: "Audio and speech recognition", E: "Assessment, report and feedback", F: "Interface and navigation",
    G: "Language and translation quality", H: "Performance and speed", I: "Re-Practice and interview controls",
    J: "Value and pricing", K: "Open feedback and follow-up",
  },
  ratings: { excellent: "Excellent", good: "Good", fair: "Fair", poor: "Poor" },
  // Hints inside comment boxes, by question id: none built in; set in Admin (2026-11-04).
  ph: {},
  q: {
    a_overall: "Overall, how would you rate this interview session?",
    a_ease: "How easy was the Simulator to use?",
    a_realistic: "Did the interview feel realistic?",
    a_useful: "How useful was this for preparing for a real job interview?",
    a_use_again: "Would you use the Simulator again?",
    a_recommend: "Would you recommend it to someone preparing for an English-language job interview?",
    b_worked: "Did everything work as expected during your session?",
    b_problem_types: "What type of problem did you experience?",
    b_describe: "Please describe what happened: what you expected, and what actually happened.",
    b_prevented: "Did the problem prevent you from completing the interview?",
    b_repeated: "Did the problem happen more than once?",
    c_relevant: "Were the questions relevant to the job you were preparing for?",
    c_level: "Were the questions appropriate for your experience level?",
    c_issue: "Were any questions confusing, repetitive, unrealistic, or inappropriate?",
    c_question_number: "Which question?",
    c_describe: "Please identify or describe the question.",
    c_what_wrong: "What was wrong with the question?",
    d_hear: "Could you clearly hear the interviewer?",
    d_voice_natural: "Did the interviewer voice sound natural?",
    d_transcription: "Did the Simulator correctly understand/transcribe your answers?",
    d_mic_issue: "Did you experience microphone or recording problems?",
    d_mic_types: "What microphone/recording problem occurred?",
    d_other: "Anything else we should know about audio or speech recognition?",
    e_consistent: "Did the assessment seem consistent with how you felt you performed?",
    e_understandable: "Was the feedback easy to understand?",
    e_useful: "Was the feedback useful?",
    e_unfair: "Did any feedback seem incorrect or unfair?",
    e_unfair_explain: "Please explain what seemed incorrect or unfair.",
    e_model_answers: "Were the suggested improved answers useful?",
    e_english_feedback: "Was the English-language feedback useful?",
    e_mismatch: "Did anything in the report fail to match what you said or what happened in the interview?",
    e_mismatch_explain: "Please describe the mismatch.",
    f_clear: "Was it clear what to do on each screen?",
    f_lost: "Did you ever feel lost or unsure what to do next?",
    f_lost_where: "Where did this happen?",
    f_unexpected: "Did any buttons, menus, instructions, or screens behave differently than expected?",
    f_unexpected_explain: "Please describe the unexpected behaviour.",
    f_layout: "Did you notice any layout or display problems?",
    g_language: "Which interface language did you use?",
    g_natural: "Did the translated interface sound natural in your language?",
    g_incorrect: "Did you notice any incorrect translations?",
    g_untranslated: "Was any text still displayed in English when it should have been translated?",
    g_regional: "Did any wording sound unnatural, confusing, or inappropriate for your country/region?",
    g_where: "Please copy the incorrect wording or tell us where you saw it.",
    g_suggestion: "What should it say instead, if you know?",
    h_speed: "How would you rate the Simulator's speed?",
    h_waits: "Did you experience unusually long waits?",
    h_where: "Where did the delay occur?",
    h_crash: "Did the app freeze, crash, reload, or disconnect?",
    h_crash_explain: "Please describe what happened.",
    i_repractice: "Did you use Re-Practice?",
    i_repractice_clear: "Was it easy to understand how Re-Practice worked?",
    i_repractice_worked: "Did Re-Practice work correctly?",
    i_repractice_problem: "What went wrong with Re-Practice?",
    i_repractice_useful: "Was the additional practice useful?",
    i_control: "Did you restart or end an interview?",
    i_control_worked: "Did that control behave as expected?",
    i_control_problem: "What happened instead?",
    j_value: "How valuable would this product be to someone preparing for a job interview in English?",
    j_would_pay: "Would you personally pay for 30 days of access?",
    j_price: "What price would you consider reasonable for 30 days of access with up to 10 interview sessions?",
    k_liked: "What did you like most about the Simulator?",
    k_improve: "What is the single most important thing we should improve?",
    k_expected: "Was there anything you expected the Simulator to do that it did not do?",
    k_other: "Any other comments or suggestions?",
    k_contact: "May we contact you if we have questions about your feedback? (We'll use your account email.)",
    k_followup: "Would you be willing to take part in a short follow-up beta interview?",
  },
  opt: {
    yes: "Yes", no: "No", maybe: "Maybe", partially: "Partially", once: "Once", more_than_once: "More than once",
    every_time: "Every time I tried", other: "Other",
    login: "Login / account", resume_upload: "Résumé upload", jd_upload: "Job description upload", setup: "Setup",
    question_generation: "Question generation", microphone: "Microphone", interviewer_audio: "Interviewer audio",
    transcription: "Speech recognition / transcription", question_display: "Question display", repractice: "Re-Practice",
    restart: "Restart", end_navigation: "End interview / navigation", report: "Assessment / report",
    pdf_download: "PDF / download", session_counter: "Session counter",
    freeze_crash: "Freeze / crash", error_message: "Error message", slow_loading: "Slow loading",
    not_relevant: "Not relevant", too_difficult: "Too difficult", too_easy: "Too easy",
    assumes_experience: "Assumes experience I don't have", repetitive: "Repetitive", more_than_one: "Asks more than one thing",
    confusing: "Confusing wording", unrealistic: "Unrealistic",
    always: "Always", usually: "Usually", sometimes: "Sometimes", rarely: "Rarely", never: "Never",
    very_accurate: "Very accurately", mostly_accurate: "Mostly accurately", some_errors: "Some errors",
    many_errors: "Many errors", could_not_tell: "Could not tell",
    mic_did_not_start: "Microphone did not start", recording_stopped: "Recording stopped", answer_cut_off: "Answer cut off",
    poor_audio: "Poor audio quality", speech_not_detected: "Speech not detected",
    text_cut_off: "Text cut off", overlapping: "Overlapping controls", off_screen: "Off-screen content",
    did_not_fit: "Page did not fit", mobile_layout: "Mobile / tablet layout", spacing: "Spacing / alignment",
    missing_image: "Missing image / icon",
    very_fast: "Very fast", fast: "Fast", acceptable: "Acceptable", slow: "Slow", very_slow: "Very slow",
    preparation: "Interview preparation", question_loading: "Question loading", between_questions: "Between questions",
    audio_generation: "Audio generation", answer_processing: "Answer processing", report_generation: "Report generation",
    restarted: "Restarted", ended: "Ended interview", neither: "Neither",
    under_15: "Under C$15", "15_19": "C$15–19.99", "20_24": "C$20–24.99", "25_29": "C$25–29.99",
    "30_39": "C$30–39.99", "40_plus": "C$40+", would_not_pay: "I would not pay",
  },
  languages: { en: "English", "pt-BR": "Português (Brasil)", "es-419": "Español", "fr-CA": "Français (Canada)" },
};

const PT = {
  ui: {
    eyebrow: "Relatório Beta",
    title: "Conte-nos sobre esta entrevista",
    intro: "Leva cerca de 3–5 minutos. Suas respostas nos ajudam a corrigir problemas e melhorar o Interview Simulator. Envie-o antes de começar sua próxima entrevista.",
    mode: "Modo da entrevista",
    modes: { guided: "Modo Guiado", live: "Modo Avançado" },
    stage: (n, total) => `Etapa ${n} de ${total}`,
    progress: "Progresso",
    next: "Próximo", back: "Voltar", later: "Preencher depois",
    submit: "Enviar Relatório Beta", submitting: "Enviando…",
    required: "Obrigatório", optional: "Opcional",
    missing: "Responda às perguntas marcadas como obrigatórias.",
    submitted: "Obrigado! Seu Relatório Beta foi enviado.",
    submitFail: "Não foi possível enviar seu Relatório Beta. Tente novamente.",
    charsLeft: (n) => `${n} caracteres restantes`,
    pickQuestion: "Número da pergunta (opcional)", noQuestion: "Não sei / várias",
    languageAuto: "Idioma da interface (detectado automaticamente)", na: "N/A",
    alreadySent: "Você já enviou o Relatório Beta desta entrevista. Obrigado!",
    backToReport: "Voltar ao relatório",
    pending: "Envie o Relatório Beta da sua última entrevista antes de começar uma nova.",
    pendingOpen: "Abrir o Relatório Beta",
    ratingWhy: "O que poderia ser melhor? Dê detalhes, por favor.",
    ratingWhyHint: "Por exemplo: o que aconteceu e em qual tela ou pergunta.",
  },
  stages: { experience: "Experiência", interview: "Entrevista", feedback: "Feedback", technical: "Técnico", final: "Comentários finais" },
  sections: {
    A: "Experiência geral", B: "Funcionamento e erros", C: "Qualidade das perguntas", D: "Áudio e reconhecimento de fala",
    E: "Avaliação, relatório e feedback", F: "Interface e navegação", G: "Idioma e tradução", H: "Desempenho e velocidade",
    I: "Repetição da prática e controles", J: "Valor e preço", K: "Comentários abertos e contato",
  },
  ratings: { excellent: "Excelente", good: "Bom", fair: "Razoável", poor: "Ruim" },
  // Hints inside comment boxes, by question id: none built in; set in Admin (2026-11-04).
  ph: {},
  q: {
    a_overall: "No geral, como você avalia esta sessão de entrevista?",
    a_ease: "Foi fácil usar o Simulador?",
    a_realistic: "A entrevista pareceu realista?",
    a_useful: "Foi útil para se preparar para uma entrevista de emprego real?",
    a_use_again: "Você usaria o Simulador novamente?",
    a_recommend: "Você o recomendaria a alguém que está se preparando para uma entrevista de emprego em inglês?",
    b_worked: "Tudo funcionou como esperado durante a sua sessão?",
    b_problem_types: "Que tipo de problema você teve?",
    b_describe: "Descreva o que aconteceu: o que você esperava e o que realmente aconteceu.",
    b_prevented: "O problema impediu você de concluir a entrevista?",
    b_repeated: "O problema aconteceu mais de uma vez?",
    c_relevant: "As perguntas eram relevantes para a vaga que você está buscando?",
    c_level: "As perguntas eram adequadas ao seu nível de experiência?",
    c_issue: "Alguma pergunta foi confusa, repetitiva, irrealista ou inadequada?",
    c_question_number: "Qual pergunta?",
    c_describe: "Identifique ou descreva a pergunta.",
    c_what_wrong: "O que havia de errado com a pergunta?",
    d_hear: "Você conseguia ouvir o entrevistador com clareza?",
    d_voice_natural: "A voz do entrevistador soou natural?",
    d_transcription: "O Simulador entendeu/transcreveu corretamente as suas respostas?",
    d_mic_issue: "Você teve problemas com o microfone ou a gravação?",
    d_mic_types: "Que problema de microfone/gravação ocorreu?",
    d_other: "Há algo mais que devemos saber sobre o áudio ou o reconhecimento de fala?",
    e_consistent: "A avaliação pareceu coerente com o seu desempenho?",
    e_understandable: "O feedback foi fácil de entender?",
    e_useful: "O feedback foi útil?",
    e_unfair: "Algum feedback pareceu incorreto ou injusto?",
    e_unfair_explain: "Explique o que pareceu incorreto ou injusto.",
    e_model_answers: "As respostas melhoradas sugeridas foram úteis?",
    e_english_feedback: "O feedback sobre o seu inglês foi útil?",
    e_mismatch: "Algo no relatório não correspondeu ao que você disse ou ao que aconteceu na entrevista?",
    e_mismatch_explain: "Descreva a diferença.",
    f_clear: "Estava claro o que fazer em cada tela?",
    f_lost: "Você se sentiu perdido ou sem saber o que fazer em seguida?",
    f_lost_where: "Onde isso aconteceu?",
    f_unexpected: "Algum botão, menu, instrução ou tela se comportou de forma inesperada?",
    f_unexpected_explain: "Descreva o comportamento inesperado.",
    f_layout: "Você notou algum problema de layout ou exibição?",
    g_language: "Qual idioma de interface você usou?",
    g_natural: "A interface traduzida soou natural no seu idioma?",
    g_incorrect: "Você notou alguma tradução incorreta?",
    g_untranslated: "Algum texto apareceu em inglês quando deveria estar traduzido?",
    g_regional: "Alguma frase soou estranha, confusa ou inadequada para o seu país/região?",
    g_where: "Copie o texto incorreto ou diga onde você o viu.",
    g_suggestion: "Como deveria ser, se você souber?",
    h_speed: "Como você avalia a velocidade do Simulador?",
    h_waits: "Você teve esperas muito longas?",
    h_where: "Onde ocorreu a demora?",
    h_crash: "O aplicativo travou, fechou, recarregou ou se desconectou?",
    h_crash_explain: "Descreva o que aconteceu.",
    i_repractice: "Você usou a repetição da prática (Re-Practice)?",
    i_repractice_clear: "Foi fácil entender como a repetição da prática funciona?",
    i_repractice_worked: "A repetição da prática funcionou corretamente?",
    i_repractice_problem: "O que deu errado na repetição da prática?",
    i_repractice_useful: "A prática adicional foi útil?",
    i_control: "Você reiniciou ou encerrou uma entrevista?",
    i_control_worked: "Esse controle funcionou como esperado?",
    i_control_problem: "O que aconteceu em vez disso?",
    j_value: "Quanto este produto valeria para alguém que está se preparando para uma entrevista de emprego em inglês?",
    j_would_pay: "Você pagaria por 30 dias de acesso?",
    j_price: "Que preço você consideraria razoável por 30 dias de acesso com até 10 sessões de entrevista?",
    k_liked: "Do que você mais gostou no Simulador?",
    k_improve: "Qual é a coisa mais importante que devemos melhorar?",
    k_expected: "Havia algo que você esperava que o Simulador fizesse e ele não fez?",
    k_other: "Outros comentários ou sugestões?",
    k_contact: "Podemos entrar em contato se tivermos dúvidas sobre o seu feedback? (Usaremos o e-mail da sua conta.)",
    k_followup: "Você aceitaria participar de uma breve entrevista beta de acompanhamento?",
  },
  opt: {
    yes: "Sim", no: "Não", maybe: "Talvez", partially: "Parcialmente", once: "Uma vez", more_than_once: "Mais de uma vez",
    every_time: "Toda vez que tentei", other: "Outro",
    login: "Login / conta", resume_upload: "Envio do currículo", jd_upload: "Envio da descrição da vaga", setup: "Configuração",
    question_generation: "Geração das perguntas", microphone: "Microfone", interviewer_audio: "Áudio do entrevistador",
    transcription: "Reconhecimento de fala / transcrição", question_display: "Exibição das perguntas", repractice: "Repetição da prática",
    restart: "Reiniciar", end_navigation: "Encerrar entrevista / navegação", report: "Avaliação / relatório",
    pdf_download: "PDF / download", session_counter: "Contador de sessões",
    freeze_crash: "Travamento / falha", error_message: "Mensagem de erro", slow_loading: "Carregamento lento",
    not_relevant: "Não relevante", too_difficult: "Difícil demais", too_easy: "Fácil demais",
    assumes_experience: "Supõe experiência que não tenho", repetitive: "Repetitiva", more_than_one: "Pergunta mais de uma coisa",
    confusing: "Redação confusa", unrealistic: "Irrealista",
    always: "Sempre", usually: "Geralmente", sometimes: "Às vezes", rarely: "Raramente", never: "Nunca",
    very_accurate: "Com muita precisão", mostly_accurate: "Quase sempre com precisão", some_errors: "Alguns erros",
    many_errors: "Muitos erros", could_not_tell: "Não sei dizer",
    mic_did_not_start: "O microfone não iniciou", recording_stopped: "A gravação parou", answer_cut_off: "A resposta foi cortada",
    poor_audio: "Áudio de baixa qualidade", speech_not_detected: "A fala não foi detectada",
    text_cut_off: "Texto cortado", overlapping: "Controles sobrepostos", off_screen: "Conteúdo fora da tela",
    did_not_fit: "A página não coube", mobile_layout: "Layout no celular / tablet", spacing: "Espaçamento / alinhamento",
    missing_image: "Imagem / ícone ausente",
    very_fast: "Muito rápido", fast: "Rápido", acceptable: "Aceitável", slow: "Lento", very_slow: "Muito lento",
    preparation: "Preparação da entrevista", question_loading: "Carregamento das perguntas", between_questions: "Entre as perguntas",
    audio_generation: "Geração do áudio", answer_processing: "Processamento das respostas", report_generation: "Geração do relatório",
    restarted: "Reiniciei", ended: "Encerrei a entrevista", neither: "Nenhum dos dois",
    under_15: "Menos de C$15", "15_19": "C$15–19,99", "20_24": "C$20–24,99", "25_29": "C$25–29,99",
    "30_39": "C$30–39,99", "40_plus": "C$40+", would_not_pay: "Eu não pagaria",
  },
  languages: EN.languages,
};

const ES = {
  ui: {
    eyebrow: "Informe Beta",
    title: "Cuéntenos sobre esta entrevista",
    intro: "Toma unos 3–5 minutos. Sus respuestas nos ayudan a corregir problemas y mejorar Interview Simulator. Envíelo antes de empezar su próxima entrevista.",
    mode: "Modo de la entrevista",
    modes: { guided: "Modo Guiado", live: "Modo Avanzado" },
    stage: (n, total) => `Paso ${n} de ${total}`,
    progress: "Progreso",
    next: "Siguiente", back: "Atrás", later: "Completar después",
    submit: "Enviar Informe Beta", submitting: "Enviando…",
    required: "Obligatorio", optional: "Opcional",
    missing: "Responda las preguntas marcadas como obligatorias.",
    submitted: "¡Gracias! Su Informe Beta fue enviado.",
    submitFail: "No pudimos enviar su Informe Beta. Inténtelo de nuevo.",
    charsLeft: (n) => `Quedan ${n} caracteres`,
    pickQuestion: "Número de la pregunta (opcional)", noQuestion: "No sé / varias",
    languageAuto: "Idioma de la interfaz (detectado automáticamente)", na: "N/A",
    alreadySent: "Ya envió el Informe Beta de esta entrevista. ¡Gracias!",
    backToReport: "Volver al informe",
    pending: "Envíe el Informe Beta de su última entrevista antes de empezar una nueva.",
    pendingOpen: "Abrir el Informe Beta",
    ratingWhy: "¿Qué podría mejorar? Por favor, dé detalles.",
    ratingWhyHint: "Por ejemplo: qué pasó y en qué pantalla o pregunta.",
  },
  stages: { experience: "Experiencia", interview: "Entrevista", feedback: "Comentarios", technical: "Técnico", final: "Comentarios finales" },
  sections: {
    A: "Experiencia general", B: "Funcionamiento y errores", C: "Calidad de las preguntas", D: "Audio y reconocimiento de voz",
    E: "Evaluación, informe y comentarios", F: "Interfaz y navegación", G: "Idioma y traducción", H: "Rendimiento y velocidad",
    I: "Repetir la práctica y controles", J: "Valor y precio", K: "Comentarios abiertos y seguimiento",
  },
  ratings: { excellent: "Excelente", good: "Bueno", fair: "Regular", poor: "Malo" },
  // Hints inside comment boxes, by question id: none built in; set in Admin (2026-11-04).
  ph: {},
  q: {
    a_overall: "En general, ¿cómo calificaría esta sesión de entrevista?",
    a_ease: "¿Qué tan fácil fue usar el Simulador?",
    a_realistic: "¿La entrevista le pareció realista?",
    a_useful: "¿Qué tan útil fue para prepararse para una entrevista de trabajo real?",
    a_use_again: "¿Volvería a usar el Simulador?",
    a_recommend: "¿Lo recomendaría a alguien que se prepara para una entrevista de trabajo en inglés?",
    b_worked: "¿Funcionó todo como esperaba durante su sesión?",
    b_problem_types: "¿Qué tipo de problema tuvo?",
    b_describe: "Describa lo que pasó: lo que esperaba y lo que realmente ocurrió.",
    b_prevented: "¿El problema le impidió completar la entrevista?",
    b_repeated: "¿El problema ocurrió más de una vez?",
    c_relevant: "¿Las preguntas eran relevantes para el puesto que busca?",
    c_level: "¿Las preguntas eran adecuadas para su nivel de experiencia?",
    c_issue: "¿Alguna pregunta fue confusa, repetitiva, poco realista o inapropiada?",
    c_question_number: "¿Qué pregunta?",
    c_describe: "Identifique o describa la pregunta.",
    c_what_wrong: "¿Qué tenía de malo la pregunta?",
    d_hear: "¿Podía escuchar claramente al entrevistador?",
    d_voice_natural: "¿La voz del entrevistador sonaba natural?",
    d_transcription: "¿El Simulador entendió/transcribió correctamente sus respuestas?",
    d_mic_issue: "¿Tuvo problemas con el micrófono o la grabación?",
    d_mic_types: "¿Qué problema de micrófono/grabación ocurrió?",
    d_other: "¿Algo más que debamos saber sobre el audio o el reconocimiento de voz?",
    e_consistent: "¿La evaluación coincidió con cómo sintió que lo hizo?",
    e_understandable: "¿Los comentarios fueron fáciles de entender?",
    e_useful: "¿Los comentarios fueron útiles?",
    e_unfair: "¿Algún comentario le pareció incorrecto o injusto?",
    e_unfair_explain: "Explique qué le pareció incorrecto o injusto.",
    e_model_answers: "¿Fueron útiles las respuestas mejoradas sugeridas?",
    e_english_feedback: "¿Fueron útiles los comentarios sobre su inglés?",
    e_mismatch: "¿Algo en el informe no coincidió con lo que dijo o con lo que pasó en la entrevista?",
    e_mismatch_explain: "Describa la diferencia.",
    f_clear: "¿Estaba claro qué hacer en cada pantalla?",
    f_lost: "¿Alguna vez se sintió perdido o sin saber qué hacer después?",
    f_lost_where: "¿Dónde pasó?",
    f_unexpected: "¿Algún botón, menú, instrucción o pantalla se comportó de forma inesperada?",
    f_unexpected_explain: "Describa el comportamiento inesperado.",
    f_layout: "¿Notó algún problema de diseño o visualización?",
    g_language: "¿Qué idioma de interfaz usó?",
    g_natural: "¿La interfaz traducida sonaba natural en su idioma?",
    g_incorrect: "¿Notó alguna traducción incorrecta?",
    g_untranslated: "¿Apareció algún texto en inglés que debía estar traducido?",
    g_regional: "¿Alguna expresión le pareció poco natural, confusa o inapropiada para su país/región?",
    g_where: "Copie el texto incorrecto o díganos dónde lo vio.",
    g_suggestion: "¿Qué debería decir, si lo sabe?",
    h_speed: "¿Cómo calificaría la velocidad del Simulador?",
    h_waits: "¿Tuvo esperas inusualmente largas?",
    h_where: "¿Dónde ocurrió la demora?",
    h_crash: "¿La aplicación se congeló, se cerró, se recargó o se desconectó?",
    h_crash_explain: "Describa lo que pasó.",
    i_repractice: "¿Usó la opción de repetir la práctica (Re-Practice)?",
    i_repractice_clear: "¿Fue fácil entender cómo funciona repetir la práctica?",
    i_repractice_worked: "¿Repetir la práctica funcionó correctamente?",
    i_repractice_problem: "¿Qué salió mal al repetir la práctica?",
    i_repractice_useful: "¿Fue útil la práctica adicional?",
    i_control: "¿Reinició o terminó una entrevista?",
    i_control_worked: "¿Ese control funcionó como esperaba?",
    i_control_problem: "¿Qué pasó en su lugar?",
    j_value: "¿Qué tan valioso sería este producto para alguien que se prepara para una entrevista de trabajo en inglés?",
    j_would_pay: "¿Pagaría usted por 30 días de acceso?",
    j_price: "¿Qué precio le parecería razonable por 30 días de acceso con hasta 10 sesiones de entrevista?",
    k_liked: "¿Qué fue lo que más le gustó del Simulador?",
    k_improve: "¿Qué es lo más importante que deberíamos mejorar?",
    k_expected: "¿Esperaba que el Simulador hiciera algo que no hizo?",
    k_other: "¿Otros comentarios o sugerencias?",
    k_contact: "¿Podemos contactarle si tenemos preguntas sobre sus comentarios? (Usaremos el correo de su cuenta.)",
    k_followup: "¿Estaría dispuesto a participar en una breve entrevista beta de seguimiento?",
  },
  opt: {
    yes: "Sí", no: "No", maybe: "Tal vez", partially: "Parcialmente", once: "Una vez", more_than_once: "Más de una vez",
    every_time: "Cada vez que lo intenté", other: "Otro",
    login: "Inicio de sesión / cuenta", resume_upload: "Carga del currículum", jd_upload: "Carga de la descripción del puesto", setup: "Configuración",
    question_generation: "Generación de preguntas", microphone: "Micrófono", interviewer_audio: "Audio del entrevistador",
    transcription: "Reconocimiento de voz / transcripción", question_display: "Visualización de preguntas", repractice: "Repetir la práctica",
    restart: "Reiniciar", end_navigation: "Terminar entrevista / navegación", report: "Evaluación / informe",
    pdf_download: "PDF / descarga", session_counter: "Contador de sesiones",
    freeze_crash: "Congelamiento / falla", error_message: "Mensaje de error", slow_loading: "Carga lenta",
    not_relevant: "No relevante", too_difficult: "Demasiado difícil", too_easy: "Demasiado fácil",
    assumes_experience: "Supone experiencia que no tengo", repetitive: "Repetitiva", more_than_one: "Pregunta más de una cosa",
    confusing: "Redacción confusa", unrealistic: "Poco realista",
    always: "Siempre", usually: "Normalmente", sometimes: "A veces", rarely: "Rara vez", never: "Nunca",
    very_accurate: "Con mucha precisión", mostly_accurate: "Casi siempre con precisión", some_errors: "Algunos errores",
    many_errors: "Muchos errores", could_not_tell: "No sé decir",
    mic_did_not_start: "El micrófono no se activó", recording_stopped: "La grabación se detuvo", answer_cut_off: "La respuesta se cortó",
    poor_audio: "Mala calidad de audio", speech_not_detected: "No se detectó la voz",
    text_cut_off: "Texto cortado", overlapping: "Controles superpuestos", off_screen: "Contenido fuera de pantalla",
    did_not_fit: "La página no cabía", mobile_layout: "Diseño en celular / tableta", spacing: "Espaciado / alineación",
    missing_image: "Falta una imagen / ícono",
    very_fast: "Muy rápido", fast: "Rápido", acceptable: "Aceptable", slow: "Lento", very_slow: "Muy lento",
    preparation: "Preparación de la entrevista", question_loading: "Carga de preguntas", between_questions: "Entre preguntas",
    audio_generation: "Generación de audio", answer_processing: "Procesamiento de respuestas", report_generation: "Generación del informe",
    restarted: "Reinicié", ended: "Terminé la entrevista", neither: "Ninguno",
    under_15: "Menos de C$15", "15_19": "C$15–19.99", "20_24": "C$20–24.99", "25_29": "C$25–29.99",
    "30_39": "C$30–39.99", "40_plus": "C$40+", would_not_pay: "No pagaría",
  },
  languages: EN.languages,
};

const FR = {
  ui: {
    eyebrow: "Rapport bêta",
    title: "Parlez-nous de cette entrevue",
    intro: "Cela prend environ 3 à 5 minutes. Vos réponses nous aident à corriger les problèmes et à améliorer Interview Simulator. Veuillez l’envoyer avant de commencer votre prochaine entrevue.",
    mode: "Mode de l’entrevue",
    modes: { guided: "Mode guidé", live: "Mode avancé" },
    stage: (n, total) => `Étape ${n} de ${total}`,
    progress: "Progression",
    next: "Suivant", back: "Retour", later: "Remplir plus tard",
    submit: "Envoyer le rapport bêta", submitting: "Envoi…",
    required: "Obligatoire", optional: "Facultatif",
    missing: "Veuillez répondre aux questions marquées comme obligatoires.",
    submitted: "Merci! Votre rapport bêta a été envoyé.",
    submitFail: "Nous n’avons pas pu envoyer votre rapport bêta. Veuillez réessayer.",
    charsLeft: (n) => `${n} caractères restants`,
    pickQuestion: "Numéro de la question (facultatif)", noQuestion: "Je ne sais pas / plusieurs",
    languageAuto: "Langue de l’interface (détectée automatiquement)", na: "S. O.",
    alreadySent: "Vous avez déjà envoyé le rapport bêta pour cette entrevue. Merci!",
    backToReport: "Retour au rapport",
    pending: "Veuillez envoyer le rapport bêta de votre dernière entrevue avant d’en commencer une nouvelle.",
    pendingOpen: "Ouvrir le rapport bêta",
    ratingWhy: "Qu’est-ce qui pourrait être mieux? Donnez des détails, s’il vous plaît.",
    ratingWhyHint: "Par exemple : ce qui s’est passé, et à quel écran ou à quelle question.",
  },
  stages: { experience: "Expérience", interview: "Entrevue", feedback: "Rétroaction", technical: "Technique", final: "Commentaires finaux" },
  sections: {
    A: "Expérience générale", B: "Fonctionnement et erreurs", C: "Qualité des questions", D: "Audio et reconnaissance vocale",
    E: "Évaluation, rapport et rétroaction", F: "Interface et navigation", G: "Langue et traduction", H: "Performance et rapidité",
    I: "Reprise de la pratique et contrôles", J: "Valeur et prix", K: "Commentaires libres et suivi",
  },
  ratings: { excellent: "Excellent", good: "Bon", fair: "Passable", poor: "Mauvais" },
  // Hints inside comment boxes, by question id: none built in; set in Admin (2026-11-04).
  ph: {},
  q: {
    a_overall: "Dans l’ensemble, comment évalueriez-vous cette séance d’entrevue?",
    a_ease: "Le Simulateur était-il facile à utiliser?",
    a_realistic: "L’entrevue vous a-t-elle semblé réaliste?",
    a_useful: "Était-ce utile pour vous préparer à une vraie entrevue d’emploi?",
    a_use_again: "Utiliseriez-vous le Simulateur de nouveau?",
    a_recommend: "Le recommanderiez-vous à quelqu’un qui se prépare à une entrevue d’emploi en anglais?",
    b_worked: "Tout a-t-il fonctionné comme prévu pendant votre séance?",
    b_problem_types: "Quel type de problème avez-vous rencontré?",
    b_describe: "Décrivez ce qui s’est passé : ce que vous attendiez et ce qui s’est réellement produit.",
    b_prevented: "Le problème vous a-t-il empêché de terminer l’entrevue?",
    b_repeated: "Le problème s’est-il produit plus d’une fois?",
    c_relevant: "Les questions étaient-elles pertinentes pour l’emploi visé?",
    c_level: "Les questions étaient-elles adaptées à votre niveau d’expérience?",
    c_issue: "Des questions étaient-elles confuses, répétitives, irréalistes ou inappropriées?",
    c_question_number: "Quelle question?",
    c_describe: "Indiquez ou décrivez la question.",
    c_what_wrong: "Quel était le problème avec la question?",
    d_hear: "Pouviez-vous entendre clairement l’intervieweur?",
    d_voice_natural: "La voix de l’intervieweur semblait-elle naturelle?",
    d_transcription: "Le Simulateur a-t-il bien compris/transcrit vos réponses?",
    d_mic_issue: "Avez-vous eu des problèmes de micro ou d’enregistrement?",
    d_mic_types: "Quel problème de micro/d’enregistrement s’est produit?",
    d_other: "Autre chose à nous dire sur l’audio ou la reconnaissance vocale?",
    e_consistent: "L’évaluation correspondait-elle à votre impression de votre performance?",
    e_understandable: "La rétroaction était-elle facile à comprendre?",
    e_useful: "La rétroaction était-elle utile?",
    e_unfair: "Une rétroaction vous a-t-elle semblé incorrecte ou injuste?",
    e_unfair_explain: "Expliquez ce qui vous a semblé incorrect ou injuste.",
    e_model_answers: "Les réponses améliorées suggérées étaient-elles utiles?",
    e_english_feedback: "La rétroaction sur votre anglais était-elle utile?",
    e_mismatch: "Quelque chose dans le rapport ne correspondait-il pas à ce que vous avez dit ou à ce qui s’est passé?",
    e_mismatch_explain: "Décrivez la différence.",
    f_clear: "Était-il clair quoi faire à chaque écran?",
    f_lost: "Vous êtes-vous déjà senti perdu ou incertain de la suite?",
    f_lost_where: "Où cela s’est-il produit?",
    f_unexpected: "Des boutons, menus, instructions ou écrans ont-ils réagi de façon inattendue?",
    f_unexpected_explain: "Décrivez le comportement inattendu.",
    f_layout: "Avez-vous remarqué des problèmes de mise en page ou d’affichage?",
    g_language: "Quelle langue d’interface avez-vous utilisée?",
    g_natural: "L’interface traduite semblait-elle naturelle dans votre langue?",
    g_incorrect: "Avez-vous remarqué des traductions incorrectes?",
    g_untranslated: "Du texte est-il resté en anglais alors qu’il aurait dû être traduit?",
    g_regional: "Une formulation vous a-t-elle semblé peu naturelle, confuse ou inappropriée pour votre pays/région?",
    g_where: "Copiez le texte incorrect ou dites-nous où vous l’avez vu.",
    g_suggestion: "Que devrait-il dire, si vous le savez?",
    h_speed: "Comment évalueriez-vous la rapidité du Simulateur?",
    h_waits: "Avez-vous eu des attentes anormalement longues?",
    h_where: "Où le délai s’est-il produit?",
    h_crash: "L’application s’est-elle figée, plantée, rechargée ou déconnectée?",
    h_crash_explain: "Décrivez ce qui s’est passé.",
    i_repractice: "Avez-vous utilisé la reprise de la pratique (Re-Practice)?",
    i_repractice_clear: "Était-il facile de comprendre le fonctionnement de la reprise?",
    i_repractice_worked: "La reprise a-t-elle fonctionné correctement?",
    i_repractice_problem: "Qu’est-ce qui n’a pas fonctionné avec la reprise?",
    i_repractice_useful: "La pratique supplémentaire était-elle utile?",
    i_control: "Avez-vous redémarré ou terminé une entrevue?",
    i_control_worked: "Ce contrôle a-t-il fonctionné comme prévu?",
    i_control_problem: "Que s’est-il passé à la place?",
    j_value: "Quelle valeur ce produit aurait-il pour quelqu’un qui se prépare à une entrevue d’emploi en anglais?",
    j_would_pay: "Paieriez-vous personnellement pour 30 jours d’accès?",
    j_price: "Quel prix jugeriez-vous raisonnable pour 30 jours d’accès avec jusqu’à 10 séances d’entrevue?",
    k_liked: "Qu’avez-vous le plus aimé du Simulateur?",
    k_improve: "Quelle est la chose la plus importante à améliorer?",
    k_expected: "Vous attendiez-vous à ce que le Simulateur fasse quelque chose qu’il n’a pas fait?",
    k_other: "D’autres commentaires ou suggestions?",
    k_contact: "Pouvons-nous vous contacter si nous avons des questions sur votre rétroaction? (Nous utiliserons le courriel de votre compte.)",
    k_followup: "Seriez-vous prêt à participer à une courte entrevue bêta de suivi?",
  },
  opt: {
    yes: "Oui", no: "Non", maybe: "Peut-être", partially: "En partie", once: "Une fois", more_than_once: "Plus d’une fois",
    every_time: "Chaque fois que j’ai essayé", other: "Autre",
    login: "Connexion / compte", resume_upload: "Téléversement du CV", jd_upload: "Téléversement de l’offre d’emploi", setup: "Configuration",
    question_generation: "Génération des questions", microphone: "Micro", interviewer_audio: "Audio de l’intervieweur",
    transcription: "Reconnaissance vocale / transcription", question_display: "Affichage des questions", repractice: "Reprise de la pratique",
    restart: "Redémarrer", end_navigation: "Terminer l’entrevue / navigation", report: "Évaluation / rapport",
    pdf_download: "PDF / téléchargement", session_counter: "Compteur de séances",
    freeze_crash: "Gel / plantage", error_message: "Message d’erreur", slow_loading: "Chargement lent",
    not_relevant: "Pas pertinente", too_difficult: "Trop difficile", too_easy: "Trop facile",
    assumes_experience: "Suppose une expérience que je n’ai pas", repetitive: "Répétitive", more_than_one: "Demande plus d’une chose",
    confusing: "Formulation confuse", unrealistic: "Irréaliste",
    always: "Toujours", usually: "Habituellement", sometimes: "Parfois", rarely: "Rarement", never: "Jamais",
    very_accurate: "Très précisément", mostly_accurate: "Plutôt précisément", some_errors: "Quelques erreurs",
    many_errors: "Beaucoup d’erreurs", could_not_tell: "Je ne sais pas",
    mic_did_not_start: "Le micro n’a pas démarré", recording_stopped: "L’enregistrement s’est arrêté", answer_cut_off: "La réponse a été coupée",
    poor_audio: "Mauvaise qualité audio", speech_not_detected: "La voix n’a pas été détectée",
    text_cut_off: "Texte coupé", overlapping: "Contrôles superposés", off_screen: "Contenu hors écran",
    did_not_fit: "La page ne s’adaptait pas", mobile_layout: "Affichage mobile / tablette", spacing: "Espacement / alignement",
    missing_image: "Image / icône manquante",
    very_fast: "Très rapide", fast: "Rapide", acceptable: "Acceptable", slow: "Lent", very_slow: "Très lent",
    preparation: "Préparation de l’entrevue", question_loading: "Chargement des questions", between_questions: "Entre les questions",
    audio_generation: "Génération de l’audio", answer_processing: "Traitement des réponses", report_generation: "Génération du rapport",
    restarted: "J’ai redémarré", ended: "J’ai terminé l’entrevue", neither: "Ni l’un ni l’autre",
    under_15: "Moins de 15 $ CA", "15_19": "15–19,99 $ CA", "20_24": "20–24,99 $ CA", "25_29": "25–29,99 $ CA",
    "30_39": "30–39,99 $ CA", "40_plus": "40 $ CA et plus", would_not_pay: "Je ne paierais pas",
  },
  languages: EN.languages,
};

const ALL = { en: EN, "pt-BR": PT, "es-419": ES, "fr-CA": FR };

// The built-in wording, before any changes published from Admin → Beta Report Wording.
export function builtInStrings(lang) {
  return ALL[lang] || EN;
}

// Wording published from Admin (2026-11-04): {lang: {"q.a_overall": "...", ...}},
// loaded by loadBetaReportWording() in ./wording.js and merged over the built-in text.
let published = {};
const merged = {};

export function setPublishedWording(text) {
  published = text || {};
  for (const k of Object.keys(merged)) delete merged[k];
}

export function applyWording(base, overrides) {
  const keys = Object.keys(overrides || {});
  if (!keys.length) return base;
  const out = { ...base };
  for (const key of keys) {
    const [group, id] = key.split(".");
    if (!id || !(group in base) || typeof base[group] !== "object") continue;
    out[group] = out[group] === base[group] ? { ...base[group] } : out[group];
    if (typeof base[group][id] === "function" || (base[group][id] && typeof base[group][id] === "object")) continue;
    out[group][id] = overrides[key];
  }
  return out;
}

export function betaReportStrings(lang) {
  const code = ALL[lang] ? lang : "en";
  if (!merged[code]) merged[code] = applyWording(ALL[code], published[code]);
  return merged[code];
}

export const ENGLISH = EN;
export const LANGS = Object.keys(ALL);
